import { Job, RecommendationCandidate } from '../models/job.model';
import { BaseService } from '../../../../../core/services/base.service';
import { UtilService } from '../../../../../core/services/util.service';
import { JobMetrics } from '../../../../public/models/jobMetrics.model';

interface JobRequest {
  name: string;
  category: string;
  subCategory: string;
  description: string;
  location: string;
  clientId: string;
  skills: Record<string, string[]>;
  countries: string[];
  region: string | null;
  minSalary: string;
  maxSalary: string;
  currency: string;
  publishNow: boolean;
}

interface JobPatchRequest {
  name: string;
  category: string;
  subCategory: string;
  description: string;
  location: string;
  clientId: string;
  skills: Record<string, string[]>;
  countries: string[];
  region: string | null;
  minSalary: string;
  maxSalary: string;
  currency: string;
  draft: boolean;
  published: boolean;
}

export const jobScopeFilter = (scope: 'active' | 'archived'): string =>
  scope === 'archived' ? 'deleted:eq:true' : 'deleted:eq:false,closed:eq:false';

export class JobService {
  static async Get(
    companyId: string,
    params?: {
      page?: number;
      size?: number;
      sort?: string;
      filter?: string;
    }
  ): Promise<{ content: Job[]; totalElements: number }> {
    const query = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';

    const response: { content?: Job[]; totalElements?: number } | undefined =
      await BaseService.Get(`/companies/${companyId}/jobs${query}`);

    if (!response) {
      return { content: [], totalElements: 0 };
    }

    const content = (
      Array.isArray(response.content) ? response.content : []
    ).map((job) => {
      if (job.region && (!job.countries || job.countries.length === 0)) {
        job.countries = [job.region];
      } else if (
        job.region &&
        job.countries &&
        job.countries.length > 0 &&
        !job.countries.includes(job.region)
      ) {
        job.countries = [...job.countries, job.region];
      }
      return job;
    });

    return {
      content,
      totalElements: response.totalElements ?? 0,
    };
  }

  static async GetJob(
    id: string,
    CallbackCatch?: (error: Error) => void
  ): Promise<Job> {
    let callbackcatch = (error: Error) => {
      if (['400', '404'].includes(error.message)) {
        UtilService.Alert(
          'Important!',
          'info',
          'The job entered does not exist'
        );
      } else {
        UtilService.Alert('Important!', 'info', 'Internal Server Error');
      }
    };

    if (CallbackCatch) {
      callbackcatch = CallbackCatch;
    }

    const job: Job = await BaseService.Get(
      `/featured/jobs/${id}`,
      callbackcatch
    );

    if (job.region && (!job.countries || job.countries.length === 0)) {
      job.countries = [job.region];
    } else if (
      job.region &&
      job.countries &&
      job.countries.length > 0 &&
      !job.countries.includes(job.region)
    ) {
      job.countries = [...job.countries, job.region];
    }

    return job;
  }

  static async GetMetrics(id: string): Promise<JobMetrics> {
    return await BaseService.Get(
      `/public/jobs/${id}/metrics`,
      (error: Error) => {
        if (['400', '404'].includes(error.message)) {
          UtilService.Alert(
            'Important!',
            'info',
            'The job entered does not exist'
          );
        } else {
          UtilService.Alert('Important!', 'info', 'Internal Server Error');
        }
      }
    );
  }

  static async Add(job: Job): Promise<Job> {
    const skillsByLevel: Record<string, string[]> = {};
    job.skills.forEach((skill) => {
      const level =
        skill.skillLevel.charAt(0).toUpperCase() + skill.skillLevel.slice(1);
      if (!skillsByLevel[level]) {
        skillsByLevel[level] = [];
      }
      skillsByLevel[level].push(skill.skillName);
    });

    const toAdd: JobRequest = {
      name: job.name,
      category: job.category,
      subCategory: job.subCategory,
      description: job.description,
      location: job.location,
      clientId: job.clientId,
      skills: skillsByLevel,
      countries: job.countries,
      region: job.region,
      minSalary: job.minSalary,
      maxSalary: job.maxSalary,
      currency: job.currency,
      publishNow: job.published,
    };
    try {
      const response = await BaseService.Post('/featured/jobs', toAdd);
      return response;
    } catch (error) {
      console.error('Error creating job:', error);
      throw error;
    }
  }

  static async Edit(job: Job, id: string): Promise<Job> {
    const skillsByLevel: Record<string, string[]> = {
      Beginner: [],
      Intermediate: [],
      Advanced: [],
      Expert: [],
    };

    const levelMapping: Record<string, string> = {
      BEGINNER: 'Beginner',
      INTERMEDIATE: 'Intermediate',
      ADVANCED: 'Advanced',
      EXPERT: 'Expert',
    };

    job.skills.forEach((skill) => {
      const properLevel = levelMapping[skill.skillLevel.toUpperCase()];
      if (properLevel) {
        skillsByLevel[properLevel].push(skill.skillName);
      }
    });

    const toUpdate: JobPatchRequest = {
      name: job.name,
      category: job.category,
      subCategory: job.subCategory,
      description: job.description,
      location: job.location,
      clientId: job.clientId,
      skills: skillsByLevel,
      countries: job.countries,
      region: job.region,
      minSalary: job.minSalary,
      maxSalary: job.maxSalary,
      currency: job.currency,
      draft: job.draft,
      published: job.published,
    };

    try {
      const response = await BaseService.Patch(
        `/featured/jobs/${id}`,
        toUpdate
      );
      return response;
    } catch (error) {
      console.error('Error updating job:', error);
      throw error;
    }
  }

  static async Publish(id: string): Promise<Job[]> {
    return await BaseService.Patch(`/jobs/${id}/featured/true`);
  }

  static async Delete(id: string): Promise<Job[]> {
    return await BaseService.Delete(`/jobs/${id}?hardDelete=false`);
  }

  static async Restore(id: string): Promise<void> {
    return await BaseService.Patch(`/jobs/${id}/restore`);
  }

  static async GetRecommendations(
    id: string,
    params?: {
      page?: number;
      size?: number;
      sort?: string;
      filter?: string;
    }
  ): Promise<{ content: RecommendationCandidate[]; totalElements: number }> {
    const query = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';

    return await BaseService.Get(
      `/featured/jobs/${id}/recommendations${query}`,
      undefined,
      (error: Error) => {
        throw error;
      },
      false
    );
  }
}
